import type { ActiveDay } from './ActiveDays'
import type { ChoiceChip } from './ChoiceChips'
import { WEEKDAYS_SHORT, addDays, firstOfMonth, formatDayShort, formatMonthShort, lastOfMonth, spanDays, weekdayIndex } from './days'
import type { StatItem, StatRow } from './StatCards'

/**
 * Общий слой итогов и фильтров чеков (ADR-18: общий домен не знает вида ресурса). Источник — AQSI (итоги считает сервер) или
 * 1С (чеки считает клиент) — приводит свои данные к одной «сводке» (`ReceiptsDigest`), а карточки, чипы, список дней и
 * кассиров строятся здесь, один раз. Деньги — в любых единицах источника: показывает функция `money`, которую даёт источник.
 */
export interface DigestKind {
  count: number
  sum: number
  /** Пояснение под суммой: «нал. 50,00 ₽ · безнал. 130,00 ₽» или «картой 150,00 ₽»; `null` — без пояснения. */
  detail: string | null
  /** Из суммы — наличными и безналичными, в тех же единицах, что `sum`; нет обоих — источник разбивки не отдаёт и карточек «Наличные»/«Безналичные» нет. */
  cash?: number
  cashless?: number
}

export interface DigestDay {
  /** День `ГГГГ-ММ-ДД`. */
  day: string
  saleCount: number
  saleSum: number
  refundCount: number
  refundSum: number
}

export interface ReceiptsDigest {
  sales: DigestKind
  refunds: DigestKind
  /** Дни, в которые по отбору были чеки. */
  days: readonly DigestDay[]
  /** Кассиры области отбора (у источника без кассира — пусто). */
  cashiers: readonly { name: string; count: number }[]
}

/** Столбец графика: день или месяц, на который нажатие открывает этот отрезок. */
export interface TrendPoint {
  key: string
  /** «15 окт» или «Окт 2026». */
  label: string
  range: { from: string; to: string }
  saleSum: number
  refundSum: number
  count: number
  /** Подпись столбца словами: «Окт 2026: приход 120 000 ₽, возвраты 3 000 ₽, 340 чеков». */
  text: string
  /** Приход в тысячах рублей для мелкой подписи над столбцом: «12,4 тыс. ₽»; пусто — меньше 50 рублей. */
  thousands: string
  /** Тот же форматировщик денег, что в `text`, — для итогов, посчитанных по столбцам (прогноз месяца). */
  money?: (value: number) => string
}

const THOUSANDS_FORMAT = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 })

/** Сумма в тысячах для подписи над столбцом графика. `unit` — сколько единиц источника в рубле (у AQSI копейки — 100). */
export function thousandsText(value: number, unit = 1): string {
  const thousands = value / unit / 1000
  return thousands < 0.05 ? '' : `${THOUSANDS_FORMAT.format(thousands)} тыс. ₽`
}

/** Периоды дольше этого графики показывают по месяцам, короче — по дням. */
export const TREND_MONTHLY_FROM_DAYS = 62

/**
 * Динамика по периоду: до двух месяцев — по дням, дольше — по месяцам. Дни без чеков в ряду есть (нулевыми столбцами внутри
 * периода выбора), чтобы график не врал о частоте; пустой ряд — период без чеков.
 */
export function trendOf(days: readonly DigestDay[], from: string, to: string, money: (value: number) => string, unit = 1): readonly TrendPoint[] {
  if (days.length === 0) return []
  const monthly = spanDays(from, to) > TREND_MONTHLY_FROM_DAYS
  const groups = new Map<string, { saleSum: number; refundSum: number; count: number }>()
  for (const item of days) {
    const key = monthly ? item.day.slice(0, 7) : item.day
    const entry = groups.get(key) ?? { saleSum: 0, refundSum: 0, count: 0 }
    entry.saleSum += item.saleSum
    entry.refundSum += item.refundSum
    entry.count += item.saleCount + item.refundCount
    groups.set(key, entry)
  }

  const keys: string[] = []
  if (monthly) {
    for (let month = firstOfMonth(from); month <= to; month = addDays(lastOfMonth(month), 1)) keys.push(month.slice(0, 7))
  } else {
    for (let day = from; day <= to; day = addDays(day, 1)) keys.push(day)
  }

  return keys.map((key) => {
    const entry = groups.get(key) ?? { saleSum: 0, refundSum: 0, count: 0 }
    const first = monthly ? `${key}-01` : key
    const range = monthly ? { from: first < from ? from : first, to: lastOfMonth(first) > to ? to : lastOfMonth(first) } : { from: key, to: key }
    const label = monthly ? `${formatMonthShort(first)} ${key.slice(0, 4)}` : formatDayShort(key)
    const text =
      entry.count === 0
        ? `${label}: чеков нет`
        : `${label}: приход ${money(entry.saleSum)}${entry.refundSum > 0 ? `, возвраты ${money(entry.refundSum)}` : ''}, ${entry.count} ${pluralRu(entry.count, 'чек', 'чека', 'чеков')}`
    return { key, label, range, ...entry, text, thousands: thousandsText(entry.saleSum, unit), money }
  })
}

/**
 * Дни месяца `today`, которые входят в среднее и в ожидаемую выручку. Закончившийся день с чеками входит всегда. Сегодняшний —
 * только когда его приход уже больше среднего по закончившимся дням: пока меньше, неполный день занизил бы среднее, а когда
 * больше — без него занижено было бы уже среднее. Один расчёт на прогноз и на отметки под графиком.
 */
export function countedDaysOf(points: readonly TrendPoint[], today: string): ReadonlySet<string> {
  const start = firstOfMonth(today)
  const finished = points.filter((point) => point.count > 0 && point.key >= start && point.key < today)
  const counted = new Set(finished.map((point) => point.key))
  const current = points.find((point) => point.key === today)
  const average = finished.length === 0 ? 0 : finished.reduce((total, point) => total + point.saleSum, 0) / finished.length
  if (current !== undefined && current.count > 0 && finished.length > 0 && current.saleSum > average) counted.add(today)
  return counted
}

/** Ожидаемая выручка месяца по учтённым дням (`countedDaysOf`). */
export interface MonthForecast {
  /** Учтённых дней месяца: закончившиеся с чеками и сегодняшний, если он уже выше среднего. */
  activeDays: number
  /** Всего дней в месяце. */
  monthDays: number
  /** Средний приход за день с чеками, в единицах столбцов графика. */
  average: number
  /** Средний приход × число дней месяца. */
  expected: number
  /** Приход месяца по всем загруженным столбцам графика, а не по выбранному отрезку. */
  monthSum: number
  money: (value: number) => string
}

/**
 * Прогноз для текущего месяца: приход учтённых дней делится на их число, среднее умножается на число дней месяца. Для прошлого
 * месяца итог уже известен, для будущего данных нет — там прогноза нет (`null`), как и когда чеков ещё не было или столбцы
 * не несут форматировщик денег.
 */
export function monthForecastOf(points: readonly TrendPoint[], month: string, today: string): MonthForecast | null {
  const start = firstOfMonth(month)
  if (firstOfMonth(today) !== start) return null
  const money = points.find((point) => point.money !== undefined)?.money
  if (money === undefined) return null
  const counted = countedDaysOf(points, today)
  const active = points.filter((point) => counted.has(point.key))
  if (active.length === 0) return null
  const average = active.reduce((total, point) => total + point.saleSum, 0) / active.length
  const monthDays = spanDays(start, lastOfMonth(start))
  const end = lastOfMonth(start)
  const monthSum = points.filter((point) => point.key >= start && point.key <= end).reduce((total, point) => total + point.saleSum, 0)
  return { activeDays: active.length, monthDays, average, expected: average * monthDays, monthSum, money }
}

/** «1 чек», «2 чека», «5 чеков». */
export function pluralRu(count: number, one: string, few: string, many: string): string {
  const rest = count % 100
  if (rest >= 11 && rest <= 14) return many
  const last = count % 10
  return last === 1 ? one : last >= 2 && last <= 4 ? few : many
}

export interface DigestOptions {
  money: (value: number) => string
  /** Сколько дней в периоде отбора; `null` — период задаёт смена. */
  periodDays: number | null
  /** Источник отдал не все чеки: итоги неполные, и это сказано в пояснениях. */
  truncated?: boolean
}

const EMPTY_CARDS: readonly StatItem[] = [
  { id: 'sale', label: 'Приход', value: '—', tone: 'income' },
  { id: 'refund', label: 'Возвраты', value: '—', tone: 'refund' },
  { id: 'total', label: 'Итого', value: '—', tone: 'total' },
  { id: 'count', label: 'Чеков', value: '—' },
  { id: 'days', label: 'Дней с чеками', value: '—' },
]

/** Крупные карточки: приход, возвраты, итог, число чеков, дней с чеками. Сводки нет — прочерк, а не нули. */
export function statCardsOf(digest: ReceiptsDigest | undefined, options: DigestOptions): readonly StatItem[] {
  if (digest === undefined) return EMPTY_CARDS
  const { sales, refunds } = digest
  // Каждая карточка — таблица «подпись — число»: сумма, наличными, картой, число чеков. Фраз нет; чего источник не отдал, того нет.
  const split = (cash: number | undefined, cashless: number | undefined): readonly StatRow[] =>
    cash === undefined || cashless === undefined ? [] : [
      { label: 'Наличными', value: options.money(cash) },
      { label: 'Картой', value: options.money(cashless) },
    ]
  const partial: readonly StatRow[] = options.truncated === true ? [{ label: 'Показаны чеки', value: 'не все' }] : []
  const balanceSplit = sales.cash === undefined || sales.cashless === undefined || refunds.cash === undefined || refunds.cashless === undefined
    ? [] : split(sales.cash - refunds.cash, sales.cashless - refunds.cashless)
  return [
    {
      id: 'sale',
      label: 'Приход',
      value: options.money(sales.sum),
      rows: [...split(sales.cash, sales.cashless), { label: 'Чеков', value: String(sales.count) }, ...partial],
      tone: 'income',
    },
    {
      id: 'refund',
      label: 'Возврат',
      value: options.money(refunds.sum),
      rows: [...split(refunds.cash, refunds.cashless), { label: 'Чеков', value: String(refunds.count) }, ...partial],
      tone: 'refund',
    },
    { id: 'total', label: 'Итого', value: options.money(sales.sum - refunds.sum), rows: balanceSplit, tone: 'total' },
    { id: 'count', label: 'Чеков', value: String(sales.count + refunds.count), rows: [{ label: 'Продаж', value: String(sales.count) }, { label: 'Возвратов', value: String(refunds.count) }] },
    {
      id: 'days',
      label: 'Дней с чеками',
      value: String(digest.days.length),
      ...(options.periodDays === null ? {} : { rows: [{ label: 'Дней в периоде', value: String(options.periodDays) }] }),
    },
  ]
}

/** Чипы вида: «Все · Продажи · Возвраты» с числами. */
export function kindChipsOf(digest: ReceiptsDigest | undefined): readonly ChoiceChip[] {
  const counted = (count: number | undefined) => (count === undefined ? {} : { count })
  return [
    { id: '', label: 'Все', ...counted(digest === undefined ? undefined : digest.sales.count + digest.refunds.count) },
    { id: 'sale', label: 'Продажи', ...counted(digest?.sales.count) },
    { id: 'refund', label: 'Возвраты', ...counted(digest?.refunds.count) },
  ]
}

/** Кассиры отбора. Выбранного, которого в списке нет (отбор сузился), оставляем: иначе его не сбросить. */
export function cashierChipsOf(digest: ReceiptsDigest | undefined, selected: string): readonly ChoiceChip[] {
  const chips: ChoiceChip[] = [{ id: '', label: 'Все кассиры' }]
  for (const cashier of digest?.cashiers ?? []) chips.push({ id: cashier.name, label: cashier.name, count: cashier.count })
  if (selected !== '' && !chips.some((chip) => chip.id === selected)) chips.push({ id: selected, label: selected })
  return chips
}

/** Дни с чеками словами: «15 окт, чт — 4 чека · 12 450,00 ₽ · возвратов 1». Новые сверху. */
export function activeDaysOf(digest: ReceiptsDigest | undefined, money: (value: number) => string): readonly ActiveDay[] {
  if (digest === undefined) return []
  return [...digest.days]
    .sort((left, right) => (left.day < right.day ? 1 : left.day > right.day ? -1 : 0))
    .map((item) => ({
      day: item.day,
      label: `${formatDayShort(item.day)}, ${WEEKDAYS_SHORT[weekdayIndex(item.day)]?.toLowerCase() ?? ''}`,
      note: [
        item.saleCount > 0 ? `${item.saleCount} ${pluralRu(item.saleCount, 'чек', 'чека', 'чеков')} · ${money(item.saleSum)}` : null,
        item.refundCount > 0 ? `возвратов ${item.refundCount} · ${money(item.refundSum)}` : null,
      ]
        .filter((part) => part !== null)
        .join(' · '),
    }))
}
